import React from 'react'
import { ILocalAudioTrack, ILocalVideoTrack } from 'agora-rtc-react'

/**
 * Track availability state information exposed to child components.
 * 
 * @remarks
 * This interface provides a simplified view of track state for UI components,
 * abstracting away internal implementation details while providing essential
 * information about track availability and errors.
 * 
 * @property isAvailable - Whether the track is currently available and ready to use
 * @property isLoading - Whether the track is currently being initialized
 * @property error - Error information if track initialization failed (optional)
 * 
 * @example
 * ```typescript
 * const { videoTrackState } = useContext(TracksContext)
 * 
 * if (videoTrackState.isLoading) {
 *   return <Spinner />
 * }
 * 
 * if (videoTrackState.error) {
 *   return <ErrorMessage message={videoTrackState.error.message} />
 * }
 * 
 * if (videoTrackState.isAvailable) {
 *   return <VideoComponent />
 * }
 * ```
 */
export interface TrackAvailability {
  /** Whether the track is currently available and ready to use */
  isAvailable: boolean
  /** Whether the track is currently being initialized */
  isLoading: boolean
  /** Error information if track initialization failed */
  error?: {
    type: 'permission_denied' | 'device_unavailable' | 'initialization_failed' | 'unknown'
    message: string
  }
}

/**
 * Tracks context interface providing access to local media tracks and their state.
 * 
 * @remarks
 * This context is populated by {@link TracksConfigure} and consumed by child components
 * to access local audio/video tracks and their availability state. It supports audio-only
 * mode when video is unavailable.
 * 
 * @property localVideoTrack - The local video track instance (null if unavailable)
 * @property localAudioTrack - The local audio track instance (null if unavailable)
 * @property videoTrackState - Video track availability state
 * @property audioTrackState - Audio track availability state
 * @property isAudioOnly - Whether the user is in audio-only mode
 * @property retryTrackInitialization - Function to retry track initialization
 * 
 * @example
 * ```typescript
 * const {
 *   localVideoTrack,
 *   localAudioTrack,
 *   videoTrackState,
 *   audioTrackState,
 *   isAudioOnly,
 *   retryTrackInitialization
 * } = useContext(TracksContext)
 * 
 * if (isAudioOnly) {
 *   console.log('User is in audio-only mode')
 * }
 * 
 * if (videoTrackState.error?.type === 'permission_denied') {
 *   // Show permission request UI
 *   <button onClick={retryTrackInitialization}>Grant Permission</button>
 * }
 * ```
 */
export interface TracksContextInterface {
  localVideoTrack: ILocalVideoTrack | null
  localAudioTrack: ILocalAudioTrack | null
  /** Video track availability state */
  videoTrackState: TrackAvailability
  /** Audio track availability state */
  audioTrackState: TrackAvailability
  /** Whether the user is in audio-only mode (audio available, video unavailable) */
  isAudioOnly: boolean
  /** Function to retry track initialization after permission changes or errors */
  retryTrackInitialization: () => Promise<void>
}
/**
 * React context that contains the local audio and video tracks. It's setup by {@link TracksConfigure}.
 */
const TracksContext = React.createContext<TracksContextInterface>(
  {} as TracksContextInterface
)

export const TracksProvider = TracksContext.Provider
export const TracksConsumer = TracksContext.Consumer
export default TracksContext
